import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import BottomNavbar from '../components/BottomNavbar';
import NotificationItem from '../components/NotificationItem';
import Spinner from '../components/Spinner';
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../utils/notificationApi';
import {
  getNotificationCache,
  refreshNotifications,
} from '../hooks/useNotificationPreloader';
import style from '../assets/style/Notifications.module.css';

const Notifications = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [searchTerm, setSearchTerm] = useState('');
  const [, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Load cached notifications on component mount
  useEffect(() => {
    loadCachedNotifications();
    // Trigger background refresh
    refreshNotifications();
  }, []);

  // Filter notifications when filters change
  useEffect(() => {
    filterNotifications();
  }, [notifications, filter, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for cache updates
  useEffect(() => {
    const interval = setInterval(loadCachedNotifications, 2000);
    return () => clearInterval(interval);
  }, []);

  const loadCachedNotifications = () => {
    try {
      const cache = getNotificationCache();
      setNotifications(cache.notifications || []);
      setUnreadCount(cache.unreadCount || 0);
      setLoading(false);
      setError(null);
      setHasMore(false);
    } catch (error) {
      console.error('Error loading cached notifications:', error);
      setError('Failed to load notifications. Please try again.');
      setLoading(false);
    }
  };

  const filterNotifications = () => {
    let filtered = [...notifications];

    // Filter by read status
    if (filter === 'unread') {
      filtered = filtered.filter((notif) => !notif.isRead);
    } else if (filter === 'read') {
      filtered = filtered.filter((notif) => notif.isRead);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((notif) =>
        notif.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNotifications(filtered);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);

      // Update local state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.notificationID === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );

      // Update unread count
      setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllAsRead(true);
      await markAllNotificationsAsRead();

      // Update local state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
      setError('Failed to mark all notifications as read.');
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    refreshNotifications();
    // Give a moment for the cache to update, then load
    setTimeout(loadCachedNotifications, 500);
  };

  const loadMoreNotifications = () => {
    if (hasMore && !loading) {
      setPage((prev) => prev + 1);
      // In a real implementation, this would load more from server
      refreshNotifications();
    }
  };

  const getFilterCounts = () => {
    const unreadCount = notifications.filter((n) => !n.isRead).length;
    const readCount = notifications.filter((n) => n.isRead).length;
    return { unreadCount, readCount, total: notifications.length };
  };

  const clearAllFilters = () => {
    setFilter('all');
    setSearchTerm('');
  };

  if (!user) {
    return (
      <div>
        <Navbar user={user} />
        <div className={style.container}>
          <div className={style.errorMessage}>
            You must be logged in to view notifications.
          </div>
        </div>
      </div>
    );
  }

  const filterCounts = getFilterCounts();
  const hasActiveFilters = filter !== 'all' || searchTerm;

  return (
    <div>
      <Navbar user={user} />
      <div className={style.container}>
        {/* Header */}
        <div className={style.header}>
          <h1 className={style.title}>Notifications</h1>
          <div className={style.headerActions}>
            <button
              className={style.refreshButton}
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh notifications"
            >
              Refresh
            </button>
            {unreadCount > 0 && (
              <button
                className={style.markAllButton}
                onClick={handleMarkAllAsRead}
                disabled={markingAllAsRead}
              >
                {markingAllAsRead ? 'Marking...' : 'Mark all as read'}
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {loading && notifications.length === 0 ? (
          <div className={style.loadingContainer}>
            <Spinner />
            <p>Loading notifications...</p>
          </div>
        ) : error ? (
          <div className={style.errorContainer}>
            <div className={style.errorMessage}>{error}</div>
            <button className={style.retryButton} onClick={handleRefresh}>
              Try Again
            </button>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className={style.emptyContainer}>
            <div className={style.emptyIcon}>
              {hasActiveFilters ? '🔍' : '🔔'}
            </div>
            <h3 className={style.emptyTitle}>
              {hasActiveFilters
                ? 'No matching notifications'
                : 'No notifications yet'}
            </h3>
            <p className={style.emptyMessage}>
              {hasActiveFilters
                ? "Try adjusting your search or filters to find what you're looking for."
                : "You'll see notifications here when you receive likes, comments, or other updates."}
            </p>
            {hasActiveFilters && (
              <button
                className={style.clearFiltersButton}
                onClick={clearAllFilters}
                style={{ marginTop: '1rem' }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div>
            <div className={style.notificationsList}>
              {filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.notificationID}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  compact={false}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className={style.loadMoreContainer}>
                <button
                  className={style.loadMoreButton}
                  onClick={loadMoreNotifications}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <BottomNavbar user={user} />
    </div>
  );
};

export default Notifications;
